import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';

import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { FieldErrors, FormProvider, FormState, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IEnvironmentComponentProps } from '../../../../models/AppState';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSurveyPermissions } from '../../../../models/hooks/useSurveyPermissions';
import { useEventLogging } from '../../../../models/Logging';
import { copyToClipboard, createContentStateWithHtmlStringValue } from '../../../../models/UiUtils';
import { IFileAttachment } from '../../../../view/models/Api';
import defaultImage from '../../../assets/default_event_image.svg';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { TabValue } from '../../../components-v2/UnifiedImageDialog/types';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../components/svgs/icons/TabSettingsIcon';
import { VolunteerIcon } from '../../../components/svgs/icons/VolunteerIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../components/Tabs';
import { useClonableSurvey } from '../../../hooks/useClonableSurvey';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { AdditionalQuestions } from '../shared/components/AdditionalQuestions';
import { useFieldStateManager } from '../shared/components/AdditionalQuestions/hooks/useFieldStateManager';
import { SectionTitle } from '../shared/components/FormFieldWithOptions/presentation';
import { ReorderQuestionsButton } from '../shared/components/ReorderQuestionsButton';
import { ReorderQuestionsModalComponent } from '../shared/components/ReorderQuestionsModal/ReorderQuestionsModal';
import { SurveyActions } from '../shared/components/SurveyActions';
import { SurveyHeader } from '../shared/components/SurveyHeader';
import { SurveySettings } from '../shared/components/SurveySettings';
import { UnsavedChangesWarning } from '../shared/components/UnsavedChangesWarning';
import { useUnsavedChanges } from '../shared/hooks/useUnsavedChanges';
import { hasCustomFormChanges } from '../shared/utils/customFormComparison';
import {
	AttendeesSettings,
	EventDetailsSettings,
	PaymentsSettings,
	PreviewEventSurveyForm,
	VisibilitySettings,
	VolunteersTab,
} from './components';
import {
	useEventRegistrationFormInitialization,
	useEventRegistrationFormValidation,
	useEventRegistrationSurveyMutations,
} from './hooks';
import { styleSheet } from './styles';
import { toVolunteerSettingsPayload } from './volunteerSettingsPayload';
import {
	computeReorderDirtyIds,
	EventRegistrationFormData,
	getSettingsSubTabErrors,
	getTabErrors,
	isValidEventDate,
	processEventTickets,
	TabValues,
	validationSchema,
} from './utils';

const { useCallback } = React;

const FALL_BACK_IMAGE = defaultImage;

interface IProps extends IEnvironmentComponentProps, IImpersonationContextComponentProps {
	className?: string;
}

interface IEditSurveyComponent {
	EventDetailsSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
	AttendeesSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
	PaymentsSettings: React.FC<{
		state: {
			enableRequestAttendeesToRSVP: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
		};
		eventData?: Api.IEventRegistrationSurvey;
		account?: Api.IAccount;
		publishableApiKeys?: Api.IStripeKeys;
		onToggleCheckChanged: () => void;
		onClickMedia: () => void;
		onClickFreeImage: () => void;
		isClone?: boolean;
		setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
	}>;
}

export interface IState {
	enableRequestAttendeesToRSVP: boolean;
	fileInputModalOpen: boolean;
	now: Date;
	selectedImage: IFileAttachment | null;
	customFormIsDisabled: boolean;
	selectedTab: TabValues;
	showEditSurveyName: boolean;
	showReorderModal: boolean;
}

const EventRegistrationSurveyBase: React.FC<IProps> & Partial<IEditSurveyComponent> = ({
	className,
	impersonationContext,
}) => {
	const userSession = useUserSession();
	const surveyPermissions = useSurveyPermissions();
	const [isQRModalOpen, setIsQRModalOpen] = React.useState(false);
	const [clearedVisibilityFieldIds, setClearedVisibilityFieldIds] = React.useState<string[]>();
	const [reorderDirtyFieldIds, setReorderDirtyFieldIds] = React.useState<string[]>();

	const [state, setState] = React.useState<IState>({
		enableRequestAttendeesToRSVP: false,
		fileInputModalOpen: false,
		now: new Date(),
		selectedImage: null,
		customFormIsDisabled: false,
		selectedTab: TabValues.Settings,
		showEditSurveyName: false,
		showReorderModal: false,
	});
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<TabValue>('upload');

	const account = impersonationContext ? impersonationContext.account : userSession.account;
	const volunteerBoardEnabled = !!account?.features?.boards?.volunteerBoard?.enabled;

	const history = useHistory();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EventRegistrationSurvey');
	const params = useParams<{ id?: string }>();
	const eventId = params?.id;

	const {
		surveyData,
		isClone,
		isEdit,
		isLoading: isSurveyLoading,
	} = useClonableSurvey<Api.IEventRegistrationSurvey>(eventId, impersonationContext, 'eventRegistration');
	const eventData = surveyData;
	const pastEvent = React.useMemo(() => {
		if (isClone) {
			return false;
		}

		// Lock on when the event ends, not when it starts. A mistyped start date would otherwise lock
		// the event it was typed into, leaving an already-distributed link pointing at a dead form.
		const eventEnd = eventData?.eventInformation?.endTime || eventData?.eventInformation?.startTime;
		return eventEnd ? moment(eventEnd).isBefore(new Date(), 'day') : false;
	}, [eventData, isClone]);

	const methods = useForm<EventRegistrationFormData>({
		defaultValues: {
			details: createContentStateWithHtmlStringValue(''),
			startDate: undefined,
			startTime: undefined,
			endDate: undefined,
			endTime: undefined,
			eventName: '',
			guestLimit: 0,
			location: '',
			maximumCapacity: 1,
			registrationDeadline: null,
			requireDeadline: false,
			requireMaximumCapacity: false,
			requirePhoneNumber: false,
			requireGuestNames: false,
			enableRequestAttendeesToRSVP: false,
			customForm: {
				name: '',
				fields: [] as Api.IFormField<string>[],
			},
			isPaidEvent: false,
			payments: {
				cost: 0,
			},
			eventTickets: [] as Api.IEventTicket[],
			ticketSortMode: Api.TicketSortMode.PriceAscending,
			timezone: userSession.user.userPreferences?.timeZone || moment.tz.guess(),
			notificationSettings: {
				isEnabled: false,
				additionalRecipients: [] as string[],
			},
			tagGenerationSettings: {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [],
			},
			showOnHomepage: false,
			// Default-on to match loaded events (form init uses `?? true`) and the public page
			// (`survey.showSurveyName !== false`). A divergent `false` here would make a brand-new event
			// created with a custom header hide its name — the opposite of every other code path.
			showSurveyName: true,
			includeCheckInQrCode: true,
			volunteerSettings: { enabled: false, volunteerTag: '' },
		},
		resolver: yupResolver(validationSchema),
	});
	const { handleSubmit, formState, getValues, watch, setValue, reset, clearErrors } = methods;
	const formValues = watch();
	const hasImages = Boolean(state.selectedImage);

	// Per-field dirty tracking lives here so the save-mutation onSuccess can call
	// resetDirtyState/updateInitialStates after the server confirms the save.
	const customFormFields = React.useMemo(() => formValues.customForm?.fields ?? [], [formValues.customForm?.fields]);
	const fieldStateManager = useFieldStateManager(customFormFields, isClone);

	const { publishableApiKeys, eventRegistrationMutation, updateSurveyEventMutation } =
		useEventRegistrationSurveyMutations({
			impersonationContext,
			history,
			errorMessages,
			logApiError,
			getValues,
			reset,
			setValue,
			clearErrors,
			setState,
			fieldStateManager,
		});

	// Use the new hooks for form initialization and validation
	useEventRegistrationFormInitialization({
		eventData,
		isClone,
		setValue,
		getValues,
		reset,
		setState,
	});

	useEventRegistrationFormValidation({
		formState: formState as FormState<EventRegistrationFormData>,
		setState,
	});

	const handleSave = () => {
		if (isInitializing) return;

		// Allow saving visibility changes for events that have ended
		// Block all other edits once the event is over
		const dirtyFields = Object.keys(formState.dirtyFields);
		const pastEventEditableFields = ['showOnHomepage', 'showSurveyName'];
		const onlyVisibilityChanged =
			dirtyFields.length > 0 && dirtyFields.every(field => pastEventEditableFields.includes(field));

		if (pastEvent && !onlyVisibilityChanged) {
			toast.error('Cannot edit an event that has ended (except visibility settings)');
			return;
		}

		const formData = getValues();

		const volunteerSettings = toVolunteerSettingsPayload(formData.volunteerSettings, volunteerBoardEnabled);

		let endTimeMoment = null;
		if (!isValidEventDate(formData)) {
			return;
		}
		if (formData.endTime && formData.endDate) {
			endTimeMoment = moment.tz(formData.endDate + ' ' + formData.endTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone);
		} else if (formData.endTime) {
			endTimeMoment = moment.tz(formData.startDate + ' ' + formData.endTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone);
		}
		const endTime = endTimeMoment ? endTimeMoment.toDate() : null;
		const startTime = moment
			.tz(formData.startDate + ' ' + formData.startTime, 'YYYY/MM/DD HH:mm:ss', formData.timezone)
			.toDate();
		// Ensure surveyForm.isEnabled is always true when saving, regardless of
		// whether the Questions tab was loaded during this session.
		const surveyFormForSave = formData.surveyForm ? { ...formData.surveyForm, isEnabled: true } : formData.surveyForm;
		const eventRegistration: Api.IEventRegistrationSurvey = {
			_type: 'EventRegistrationSurvey',
			attendeeOptions: {
				enabled: formData.enableRequestAttendeesToRSVP,
				// For paid events with tiered tickets, guestLimit should not be used (set to null)
				// For free events or legacy paid events, use the configured guestLimit or null
				// guestLimit of 0 means "Allow Guests" is off — send null so the backend doesn't reject it
				guestLimit:
					formData.isPaidEvent && formData.eventTickets && formData.eventTickets.length > 0
						? null
						: formData.enableRequestAttendeesToRSVP && formData.guestLimit > 0
							? formData.guestLimit
							: null,
				maximumCapacity:
					formData.enableRequestAttendeesToRSVP && !formData.isPaidEvent && formData.requireMaximumCapacity
						? formData.maximumCapacity
						: null,
				registrationDeadline: formData.enableRequestAttendeesToRSVP
					? formData.registrationDeadline
						? new Date(formData.registrationDeadline)
						: null
					: null,
				requirePhoneNumber: formData.enableRequestAttendeesToRSVP ? formData.requirePhoneNumber : false,
				requireGuestNames: formData.enableRequestAttendeesToRSVP ? formData.requireGuestNames : false,
				// Only send costPerAttendee for legacy events (no tickets), otherwise backend creates duplicate tickets
				costPerAttendee:
					formData.isPaidEvent && (!formData.eventTickets || formData.eventTickets.length === 0)
						? formData.payments.cost * 100
						: null,
				eventTickets: processEventTickets(formData.eventTickets || [], isClone),
				ticketSortMode: formData.ticketSortMode,
			},
			eventInformation: {
				details: formData.details?.getPlainTextPreview?.() || '',
				detailsContent: formData.details?.getRawRichTextContent?.(),
				endTime,
				location: formData.location,
				startTime,
				timeZone: formData.timezone,
			},
			name: formData.eventName,
			customFormIsDisabled: state.customFormIsDisabled,
			isPaidEvent: formData.isPaidEvent,
			notificationSettings: {
				_type: 'SurveyNotificationSettings',
				isEnabled: formData.notificationSettings.isEnabled,
				additionalRecipients: formData.notificationSettings.additionalRecipients.filter(
					recipient => recipient && recipient.trim() !== ''
				),
			},
			customForm: {
				name: isClone ? eventData?.name : formData.customForm.name,
				fields: formData.customForm.fields,
			},
			tagGenerationSettings: {
				tagOnCompletion: formData.tagGenerationSettings.tagOnCompletion,
				completionTag: formData.tagGenerationSettings.completionTag,
				customFormFieldTagSettings: formData.tagGenerationSettings.customFormFieldTagSettings,
			},
			showOnHomepage: formData.showOnHomepage,
			showSurveyName: formData.showSurveyName === true,
			includeCheckInQrCode: formData.includeCheckInQrCode !== false,
			surveyForm: surveyFormForSave,
			...(volunteerSettings ? { volunteerSettings } : {}),
		};
		if (state.selectedImage) {
			eventRegistration.eventInformation.image = state.selectedImage;
		}

		const needsOptions = formData.customForm.fields?.some((field: Api.IFormField<string>) => {
			return (
				(field.fieldType === Api.FormFieldType.Option || field.fieldType === Api.FormFieldType.MultipleOptions) &&
				field.options?.length > 0 &&
				field.options.find(option => option.label.trim().length === 0)
			);
		});
		const needsFieldsLabel = formData.customForm.fields.some(
			field => field.label.trim().length === 0 && field.fieldType !== Api.FormFieldType.String
		);

		if (needsFieldsLabel) {
			errorMessages.push({
				messages: ['Please add a label to all fields before saving.'],
			});
			return;
		}

		if (needsOptions) {
			errorMessages.push({
				messages: ['Please add options to all fields that are of type "Checkbox" or "Dropdown" before saving.'],
			});
			toast.error('Event not saved');
			return;
		}

		if (isEdit && !isClone) {
			// When editing existing survey (not cloning), merge with original data
			const payload = {
				...eventData,
				...eventRegistration,
			};
			updateSurveyEventMutation.mutate({ eventRegistration: payload, id: eventId });
			return;
		}
		eventRegistrationMutation.mutate({
			eventRegistration,
		});
	};

	// Without an onInvalid handler, a failed yup validation silently aborts the submit and
	// Save appears to do nothing. Toast and switch to the tab holding the error so it's visible.
	const onInvalid = (errors: FieldErrors<EventRegistrationFormData>) => {
		const errorKeys = Object.keys(errors);
		const hasCustomFormError = errorKeys.some(
			key => key.startsWith('customForm') || key === 'surveyFormVisibilityValidation'
		);
		if (hasCustomFormError) {
			setSelectedTab(TabValues.Questions);
		} else {
			setSelectedTab(TabValues.Settings);
		}
		toast.error('Some fields need attention before saving — see the highlighted tab.');
	};

	// Track if we just finished loading to ignore false positives from HtmlEditor initialization
	const [isInitializing, setIsInitializing] = React.useState(true);
	const hasResetForSurvey = React.useRef<string | null>(null);

	React.useEffect(() => {
		if (isSurveyLoading) return;

		const surveyId = surveyData?.id;
		if (surveyData && hasResetForSurvey.current !== surveyId) {
			// Mark that we're handling this survey to prevent duplicate resets
			hasResetForSurvey.current = surveyId;

			// Give HtmlEditor time to finish its initialization before considering changes
			// 1000ms timeout is based on manual testing showing HtmlEditor (TinyMCE) typically
			// initializes within 500-800ms. The 1000ms value provides headroom for slower machines.
			// Future improvement: Use HtmlEditor's onLoad callback instead of timeout (requires refactoring form components)
			const timer = setTimeout(() => {
				// Reset the form with current values to clear dirty state without changing values
				// This updates both the values and default values, marking everything as clean
				const currentValues = getValues();
				reset(currentValues);
				setIsInitializing(false);
			}, 1000);
			return () => clearTimeout(timer);
		}

		// New-event flow: no surveyData to load, so the HtmlEditor warm-up guard
		// doesn't apply. Clear isInitializing immediately so handleSave isn't blocked.
		if (!surveyData) {
			setIsInitializing(false);
		}
	}, [isSurveyLoading, surveyData, getValues, reset]);

	// Track if customForm fields have meaningful changes by comparing specific properties
	const customFormFieldsHaveChanges = React.useMemo(
		() => hasCustomFormChanges(eventData?.customForm?.fields, formValues.customForm?.fields),
		[eventData?.customForm?.fields, formValues.customForm?.fields]
	);

	const hasUnsavedChanges = useUnsavedChanges({
		surveyType: 'event',
		isEdit,
		isClone,
		isInitializing,
		formState,
		additionalConditions: [
			formValues.enableRequestAttendeesToRSVP !== eventData?.attendeeOptions?.enabled,
			state.selectedImage?.url !== eventData?.eventInformation?.image?.url,
			customFormFieldsHaveChanges,
			fieldStateManager.dirtyFields.size > 0,
			(formValues.tagGenerationSettings?.completionTag ?? '') !==
				(eventData?.tagGenerationSettings?.completionTag ?? ''),
			(formValues.tagGenerationSettings?.tagOnCompletion ?? false) !==
				(eventData?.tagGenerationSettings?.tagOnCompletion ?? false),
		],
	});

	const onClickMedia = () => {
		setUnifiedDialogTab('upload');
		setUnifiedDialogOpen(true);
	};

	const onClickFreeImage = () => {
		setUnifiedDialogTab('free-images');
		setUnifiedDialogOpen(true);
	};

	const onCopyLinkClicked = React.useCallback(() => {
		if (copyToClipboard(eventData?.anonymousLink)) {
			toast.success('Link copied to clipboard');
			return;
		}

		toast.error('Error copying link to clipboard');
	}, [eventData?.anonymousLink]);

	const setSelectedTab = (value: TabValues) => {
		setState(prevState => ({
			...prevState,
			selectedTab: value,
		}));
	};

	const onToggleCheckChanged = () => {
		setState(prevState => ({
			...prevState,
			enableRequestAttendeesToRSVP: !prevState.enableRequestAttendeesToRSVP,
		}));
	};

	// Computed values for preview section
	const formattedAmount = React.useMemo(() => {
		// For tiered tickets, show the cheapest ticket price
		if (formValues?.eventTickets && formValues.eventTickets.length > 0) {
			const cheapestTicket = formValues.eventTickets.reduce(
				(min, ticket) => ((ticket.unitPrice || 0) < (min.unitPrice || 0) ? ticket : min),
				formValues.eventTickets[0]
			);
			if (cheapestTicket?.unitPrice) {
				const dollarAmount = new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
				}).format(cheapestTicket.unitPrice / 100);
				return dollarAmount;
			}
		}
		// Legacy: use payments.cost
		if (formValues?.payments?.cost) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(formValues?.payments?.cost);
			return dollarAmount;
		}
		return '$0.00';
	}, [formValues?.payments?.cost, formValues?.eventTickets]);

	const isEventDateValid = React.useMemo(() => {
		return isValidEventDate(formValues);
	}, [formValues]);

	const eventDateTimeText = React.useMemo(() => {
		if (!isEventDateValid) return '';

		if (formValues.endDate && formValues.endTime && formValues.endDate !== formValues.startDate) {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} - ${moment.tz(formValues.endDate + ' ' + formValues.endTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		} else if (formValues.endTime) {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} - ${moment(formValues.endTime, 'HH:mm:ss').format('hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		} else {
			return `${moment.tz(formValues.startDate + ' ' + formValues.startTime, 'YYYY/MM/DD HH:mm:ss', formValues.timezone).format('MMM Do, YYYY hh:mm a')} / ${moment.tz(formValues.timezone).format('z')}`;
		}
	}, [formValues, isEventDateValid]);

	const detailsText = React.useMemo(() => {
		// Extract HTML from editor state object
		const text = formValues?.details?.getRawRichTextContent?.()?.document;
		return text || null;
	}, [formValues?.details]);

	const cleanedDetails = React.useMemo(() => {
		if (!detailsText) return '';
		// Remove lev-content tags
		const textWithoutLevContent = detailsText.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// Remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return textWithoutPStyles;
	}, [detailsText]);

	const { settings: settingsErrors, questions: customFormErrors } = getTabErrors(formState.errors);
	const settingsSubTabErrors = getSettingsSubTabErrors(formState.errors);

	const onQRCodeClick = () => {
		setIsQRModalOpen(true);
	};

	const handleQRModalClose = () => {
		setIsQRModalOpen(false);
	};

	const handleReorderQuestions = () => {
		setState(prevState => ({ ...prevState, showReorderModal: true }));
	};

	// After AdditionalQuestions consumes a reorder event, drop the stale ids so a later
	// unmount/remount of that subtree can't replay the same event.
	const handleReorderEventConsumed = useCallback(() => {
		setClearedVisibilityFieldIds(undefined);
		setReorderDirtyFieldIds(undefined);
	}, []);

	const handleReorderModalClose = useCallback(
		(result?: { reorderedQuestions?: any; clearedVisibilityFieldIds?: string[] }, cancel?: boolean) => {
			setState(prevState => ({ ...prevState, showReorderModal: false }));
			if (!cancel && result?.reorderedQuestions) {
				// Update customForm fields to the new order locally (user must click Save to persist)
				const currentFields = getValues().customForm?.fields || [];
				const currentFieldsMap = new Map(currentFields.map(field => [field.id, field]));
				const reorderedCompleteFields = result.reorderedQuestions
					.map((field: Api.IFormField<string>) => currentFieldsMap.get(field.id))
					.filter(Boolean) as Api.IFormField<string>[];

				// Determine which fields changed position so they can be marked dirty.
				// Compute against the modal's raw ordering (not the post-filter list) so dropped
				// ids can't shift surviving indices and produce false positives/negatives.
				const dirtyFromReorder = computeReorderDirtyIds(currentFields, result.reorderedQuestions);

				setValue('customForm.fields', reorderedCompleteFields);

				// Mark reordered fields as dirty (includes cleared visibility fields)
				const allDirtyIds = new Set(dirtyFromReorder);
				if (result.clearedVisibilityFieldIds?.length) {
					for (const id of result.clearedVisibilityFieldIds) {
						allDirtyIds.add(id);
					}
					setClearedVisibilityFieldIds(result.clearedVisibilityFieldIds);
				}
				if (allDirtyIds.size > 0) {
					setReorderDirtyFieldIds(Array.from(allDirtyIds));
				}
			}
		},
		[getValues, setValue]
	);

	const questions = formValues?.customForm?.fields || [];
	const hasMultipleQuestions = questions.length > 1;

	const breadcrumbPageTitle = useCallback(() => {
		if (isClone) {
			return 'Clone Event';
		}
		return params.id ? (eventData?.name ?? 'Edit Event') : 'New Event';
	}, [params.id, eventData?.name, isClone]);
	return (
		<Page styles={[className ?? '', styleSheet.page]}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/surveys/event'>Surveys</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{breadcrumbPageTitle()}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
			/>
			{pastEvent ? (
				<h2 className={css(styleSheet.notEditableNotice)}>
					This event has ended and it&apos;s no longer editable (except for visibility settings)
				</h2>
			) : null}
			<FormProvider {...methods}>
				<form className='form-container' noValidate onSubmit={handleSubmit(handleSave, onInvalid)}>
					<div className={css(styleSheet.fieldset, styleSheet.formStyles)}>
						<div className={css(styleSheet.header)}>
							<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
								<SurveyHeader
									surveyName={formValues.eventName || 'Untitled Event'}
									showEditName={state.showEditSurveyName}
									onEditToggle={() => setState(prev => ({ ...prev, showEditSurveyName: !prev.showEditSurveyName }))}
									onNameChange={name => setValue('eventName', name)}
									validationError={formState.errors?.eventName?.message}
									isEdit={isEdit}
									fieldName='eventName'
								/>
							</fieldset>
							<SurveyActions
								onCopyLink={onCopyLinkClicked}
								isLoading={eventRegistrationMutation?.isPending || updateSurveyEventMutation?.isPending}
								isSaveDisabled={state.showEditSurveyName}
								showCopyLink={!!eventData?.id}
								surveyLink={eventData?.anonymousLink}
								onQRCodeClick={onQRCodeClick}
								showViewReport={!!eventData?.id && surveyPermissions.canViewReportingEventRegistrationSurvey()}
								surveyId={eventData?.id}
								surveyType='EventRegistrationSurvey'
							/>
						</div>
						<UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges && !isSurveyLoading} />
						<TabRoot
							className={`tab-view ${css(styleSheet.tabViewContainer)}`}
							value={state.selectedTab}
							onValueChange={tValue => setSelectedTab(tValue as TabValues)}
						>
							<TabList styles={[styleSheet.tabList]}>
								<IconTabTrigger
									value={TabValues.Settings}
									icon={<TabSettingsIcon fillColor='currentColor' />}
									styles={settingsErrors ? [styleSheet.tabTextError] : []}
								>
									Settings
								</IconTabTrigger>
								<IconTabTrigger
									value={TabValues.Questions}
									styles={customFormErrors ? [styleSheet.tabTextError] : []}
									icon={<ListOfQuestionsIcon fillColor='currentColor' />}
								>
									Questions
								</IconTabTrigger>
								{volunteerBoardEnabled ? (
									<IconTabTrigger value={TabValues.Volunteers} icon={<VolunteerIcon fill='currentColor' />}>
										Volunteers
									</IconTabTrigger>
								) : null}
								{eventData?.anonymousLink ? (
									<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
										Preview
									</IconTabTrigger>
								) : null}
							</TabList>
							<TabContent
								forceMount
								value={TabValues.Settings}
								className={css(
									styleSheet.tabContent,
									state.selectedTab !== TabValues.Settings && styleSheet.tabContentHidden
								)}
							>
								<div className={css(styleSheet.introText)}>
									<h3>Event Settings</h3>
									<p className={css(styleSheet.eventLabel)}>
										Once attendees RSVP, you can send personalized follow-up emails based on their response, whether
										they&apos;re attending or not. If you send the invitation through Levitate, you&apos;ll also be able
										to see who hasn&apos;t responded yet.
									</p>
								</div>
								<SurveySettings
									styles={[styleSheet.settingsContainer]}
									tabListStyles={[styleSheet.settingsTabList]}
									tabs={[
										{
											title: 'Event Details',
											key: 'event-details',
											hasError: settingsSubTabErrors['event-details'],
											content: (
												<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0 }}>
													<EventDetailsSettings
														state={{
															enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
															now: state.now,
															selectedImage: state.selectedImage,
														}}
														eventData={eventData}
														account={account}
														publishableApiKeys={publishableApiKeys}
														onToggleCheckChanged={onToggleCheckChanged}
														onClickMedia={onClickMedia}
														onClickFreeImage={onClickFreeImage}
														isClone={isClone}
														setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
													/>
												</fieldset>
											),
										},
										{
											title: 'Attendees',
											key: 'attendees',
											hasError: settingsSubTabErrors.attendees,
											content: (
												<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0 }}>
													<AttendeesSettings
														state={{
															enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
															now: state.now,
															selectedImage: state.selectedImage,
														}}
														eventData={eventData}
														account={account}
														publishableApiKeys={publishableApiKeys}
														onToggleCheckChanged={onToggleCheckChanged}
														onClickMedia={onClickMedia}
														onClickFreeImage={onClickFreeImage}
														isClone={isClone}
														setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
													/>
												</fieldset>
											),
										},
										{
											title:
												account?.features?.stripePayment?.enabled && Boolean(publishableApiKeys)
													? 'Payments & Notifications'
													: 'Notifications',
											key: 'payments-notifications',
											hasError: settingsSubTabErrors['payments-notifications'],
											content: (
												<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0 }}>
													<PaymentsSettings
														state={{
															enableRequestAttendeesToRSVP: state.enableRequestAttendeesToRSVP,
															now: state.now,
															selectedImage: state.selectedImage,
														}}
														eventData={eventData}
														account={account}
														publishableApiKeys={publishableApiKeys}
														onToggleCheckChanged={onToggleCheckChanged}
														onClickMedia={onClickMedia}
														onClickFreeImage={onClickFreeImage}
														isClone={isClone}
														setState={setState as (state: React.Dispatch<React.SetStateAction<IState>>) => void}
													/>
												</fieldset>
											),
										},
										{
											title: 'Visibility',
											key: 'visibility',
											content: (
												<fieldset disabled={false} style={{ border: 'none', padding: 0, margin: 0 }}>
													<VisibilitySettings />
												</fieldset>
											),
										},
									]}
								/>
							</TabContent>
							<TabContent
								forceMount
								value={TabValues.Questions}
								className={css(
									styleSheet.tabContent,
									state.selectedTab !== TabValues.Questions && styleSheet.tabContentHidden
								)}
							>
								<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0 }}>
									<div className={css(styleSheet.formContainer)}>
										<div className={css(styleSheet.bodyBackground)} />
										<section className={css(styleSheet.sectionHeader)}>
											<SectionTitle
												title='Additional Questions'
												blurb='Once the survey is saved, the question type cannot be changed.'
											/>
										</section>
										<ReorderQuestionsButton
											onReorderQuestions={handleReorderQuestions}
											hasMultipleQuestions={hasMultipleQuestions}
											isLoading={eventRegistrationMutation.isPending || updateSurveyEventMutation.isPending}
										/>
										<div className={css(styleSheet.additionalQuestionsContainer)}>
											<AdditionalQuestions
												isEdit={isEdit}
												isClone={isClone}
												fieldStateManager={fieldStateManager}
												clearedVisibilityFieldIds={clearedVisibilityFieldIds}
												reorderDirtyFieldIds={reorderDirtyFieldIds}
												onReorderEventConsumed={handleReorderEventConsumed}
											/>
										</div>
									</div>
								</fieldset>
							</TabContent>
							{volunteerBoardEnabled ? (
								<TabContent
									forceMount
									value={TabValues.Volunteers}
									className={css(
										styleSheet.tabContent,
										state.selectedTab !== TabValues.Volunteers && styleSheet.tabContentHidden
									)}
								>
									<fieldset disabled={pastEvent} style={{ border: 'none', padding: 0, margin: 0 }}>
										<VolunteersTab
											surveyId={eventId}
											persistedVolunteerEnabled={!!eventData?.volunteerSettings?.enabled}
											impersonationContext={impersonationContext}
										/>
									</fieldset>
								</TabContent>
							) : null}
							{eventData?.anonymousLink ? (
								<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent)}>
									<div className={css(styleSheet.bodyBackground)} />
									<div className={css(styleSheet.surveyContainer)}>
										{formValues.showSurveyName === true && formValues.eventName ? (
											<h2 className={css(styleSheet.surveyTitle)}>{formValues.eventName}</h2>
										) : null}
										<div className={css(styleSheet.eventDetailsContainer)}>
											<figure className={css(styleSheet.previewImageContainer)}>
												<img
													className={css(styleSheet.previewImage)}
													src={hasImages ? state?.selectedImage?.url : FALL_BACK_IMAGE}
													alt={`event graphic for ${formValues?.eventName}`}
												/>
											</figure>
											<section className={css(styleSheet.details)}>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>When</h4>
													<p>{eventDateTimeText}</p>
												</div>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>Where</h4>
													<p className={css(styleSheet.locationAreaWrap)}>{formValues?.location}</p>
												</div>
												<div>
													<h4 className={css(styleSheet.eventDetailsTitle)}>Details</h4>
													<div
														className={css(styleSheet.detailsAreaWrap)}
														dangerouslySetInnerHTML={{ __html: cleanedDetails }}
													/>
												</div>
												{/* Only show Cost section for legacy events (costPerAttendee), not tiered tickets */}
												{formValues?.isPaidEvent &&
												formValues?.payments?.cost &&
												(!formValues?.eventTickets || formValues.eventTickets.length === 0) ? (
													<div>
														<h4 className={css(styleSheet.eventDetailsTitle)}>Cost</h4>
														<p className={css(styleSheet.eventPrice)}>{formattedAmount}/ticket</p>
													</div>
												) : null}
											</section>
										</div>

										<PreviewEventSurveyForm
											survey={eventData}
											formValues={formValues}
											enableRequestAttendeesToRSVP={state.enableRequestAttendeesToRSVP}
										/>
									</div>
								</TabContent>
							) : null}
						</TabRoot>
					</div>
				</form>
			</FormProvider>

			<UnifiedImageDialog
				showImageLibraryTab
				offerSaveToLibrary
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={state.selectedImage || undefined}
				impersonationContext={impersonationContext}
				onImageSelected={(image: Api.IFileAttachment) => {
					setState(prevState => ({ ...prevState, selectedImage: image }));
				}}
			/>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>

			<ReorderQuestionsModalComponent
				modalProps={{
					isOpen: state.showReorderModal,
					onRequestClose: handleReorderModalClose,
				}}
				questions={questions}
				surveyId={eventId || ''}
				impersonationContext={impersonationContext}
				surveyForm={formValues.surveyForm}
			/>
			<QRCodeModal
				modalProps={{
					isOpen: isQRModalOpen,
					onRequestClose: handleQRModalClose,
				}}
				url={eventData?.anonymousLink || ''}
				title='QR Code'
				filename={`survey-qr-${eventData?.id || 'code'}`}
			/>
		</Page>
	);
};

export const EventRegistrationSurvey = inject(ImpersonationContextKey)(EventRegistrationSurveyBase);

EventRegistrationSurvey.EventDetailsSettings = EventDetailsSettings;
EventRegistrationSurvey.AttendeesSettings = AttendeesSettings;
EventRegistrationSurvey.PaymentsSettings = PaymentsSettings;
